import { AxiosResponse } from "axios";

import { BaseService } from "../services/base-service";
import { URLS } from "../services/app-urls";

export interface DownloadOptions {
  fileName?: string;
  additionalHeaders?: Record<string, string>;
  timeout?: number;
}

export interface DownloadResult {
  success: boolean;
  fileName?: string;
  error?: string;
  blocked?: boolean;
  blobUrl?: string;
}

const baseService = new BaseService("download");
const TECHNICAL_DOCUMENTATION_FALLBACK_FILENAME = "documentacao-tecnica-plan-mat.pdf";

let cachedBlobUrl: string | null = null;
let cachedFileName: string | null = null;
let inFlight: Promise<{ blobUrl: string; fileName: string }> | null = null;

export const validateExportResponse = (data: any): { isValid: boolean; error?: string } => {
  if (!data.id) {
    return { isValid: false, error: "ID da exportação não encontrado" };
  }

  // Para arquivo já existente, deve ter status COMPLETED e file_url
  if (data.status === "COMPLETED" && !data.file_url) {
    return { isValid: false, error: "URL do arquivo não encontrada para exportação concluída" };
  }

  return { isValid: true };
};

const getFileNameFromResponse = (response: AxiosResponse): string | null => {
  const contentDisposition = response.headers["content-disposition"];
  if (contentDisposition) {
    const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (match && match[1]) {
      return match[1].replace(/['"]/g, "");
    }
  }
  return null;
};

export async function downloadExportFile(
  fileUrl: string,
  options: DownloadOptions = {},
  type: "analysis" | "materials-demands" | "po-components" | "stock" = "analysis"
): Promise<DownloadResult> {
  try {
    const fileName = fileUrl.split("/").pop() || "";

    if (!fileName) {
      throw new Error("Não foi possível extrair o nome do arquivo da URL");
    }
    const downloadUrl = buildDownloadURL(fileName, type);

    const { fileName: customFileName } = options;
    const response = await baseService.getBlob(downloadUrl);

    if (!response || response.status < 200 || response.status >= 300) {
      throw new Error(`Erro HTTP: ${response?.status || "Resposta inválida"}`);
    }

    const finalFileName = customFileName || getFileNameFromResponse(response) || fileName;

    const blobUrl = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = finalFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => {
      URL.revokeObjectURL(blobUrl);
    }, 3000);

    return {
      success: true,
      fileName: finalFileName,
    };
  } catch (error) {
    console.error("Erro no download de exportação:", error);

    return {
      success: false,
      error: error instanceof Error ? error.message : "Erro no download de exportação",
    };
  }
}

const fetchTechnicalDocumentationBlob = async (): Promise<{ blobUrl: string; fileName: string }> => {
  const response = await baseService.getBlob(URLS.TECHNICAL_DOCUMENTATION);

  if (!response || response.status < 200 || response.status >= 300) {
    throw new Error(`Erro HTTP: ${response?.status || "Resposta inválida"}`);
  }

  const fileName = getFileNameFromResponse(response) || TECHNICAL_DOCUMENTATION_FALLBACK_FILENAME;
  const blobUrl = URL.createObjectURL(response.data);

  cachedBlobUrl = blobUrl;
  cachedFileName = fileName;

  return { blobUrl, fileName };
};

export function prefetchTechnicalDocumentation(): void {
  if (cachedBlobUrl || inFlight) return;

  const request = fetchTechnicalDocumentationBlob()
    .catch((error) => {
      console.error("Erro ao pré-carregar documentação técnica:", error);
      cachedBlobUrl = null;
      cachedFileName = null;
      throw error;
    })
    .finally(() => {
      inFlight = null;
    });

  inFlight = request;
  request.catch(() => undefined);
}

export function openTechnicalDocumentation(): Promise<DownloadResult> | DownloadResult {
  if (cachedBlobUrl) {
    return openBlobInNewTab(cachedBlobUrl, cachedFileName);
  }

  const pending = inFlight ?? fetchTechnicalDocumentationBlob();
  return pending
    .then(({ blobUrl, fileName }) => openBlobInNewTab(blobUrl, fileName))
    .catch((error) => {
      console.error("Erro ao abrir documentação técnica:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Erro ao abrir documentação técnica",
      };
    });
}

function openBlobInNewTab(blobUrl: string, fileName: string | null): DownloadResult {
  const popup = window.open(blobUrl, "_blank");

  if (!popup) {
    return {
      success: false,
      blocked: true,
      blobUrl,
      error: "Não foi possível abrir a nova aba (bloqueada pelo navegador)",
    };
  }

  return {
    success: true,
    fileName: fileName ?? undefined,
  };
}

export function releaseTechnicalDocumentation(): void {
  if (cachedBlobUrl) {
    URL.revokeObjectURL(cachedBlobUrl);
  }
  cachedBlobUrl = null;
  cachedFileName = null;
  inFlight = null;
}

const buildDownloadURL = (fileName: string, type: "analysis" | "materials-demands" | "po-components" | "stock"): string => {
  if (type === "analysis") {
    return `${URLS.ANALYSIS_EXPORT}download/?filename=${encodeURIComponent(fileName)}`;
  } else if (type === "materials-demands") {
    return `${URLS.MATERIALS_DEMANDS}export/download/?filename=${encodeURIComponent(fileName)}`;
  } else if (type === "po-components") {
    return `${URLS.PO_COMPONENT}export/download/?filename=${encodeURIComponent(fileName)}`;
  } else if (type === "stock") {
    return `${URLS.STOCK_EXPORT}download/?filename=${encodeURIComponent(fileName)}`;
  }
  throw new Error("Tipo de exportação inválido");
};

export const buildAnalysisFileName = (analysisId: number): string => {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  return `Analise_GDS_${analysisId}_${yyyy}-${mm}-${dd}.xlsx`;
};

export const buildMaterialsDemandsFileName = (): string => {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  return `Materials_Demands_${yyyy}-${mm}-${dd}.xlsx`;
};

export const buildPoComponentsFileName = (withFilters = false): string => {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  const suffix = withFilters ? "_with-filters" : "";
  return `Product_orders_${yyyy}-${mm}-${dd}${suffix}.xlsx`;
};

export const buildStockFileName = (withFilters = false): string => {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  const suffix = withFilters ? "_with-filters" : "";
  return `Stock_${yyyy}-${mm}-${dd}${suffix}.xlsx`;
};
